import { alpha, darken, getContrastRatio, lighten } from "@mui/material/styles"

export const DARK_THEME = {
  palette: {
    mode: "dark",
    primary: {
      // Dark accent = bright gold with dark ink (mirrors --ct-accent in dark)
      main: "#F6AB36",
      solidBg: "#F6AB36",
      solidActiveBg: "#cf8f2a",
      solidHoverBg: "#ffbf50",
      solidColor: "#201503",
      solidDisabledBg: "#AC7725",
      solidDisabledColor: "#201503",
      outlinedColor: "#F6AB36",
      outlinedBorder: "#F6AB36",
      outlinedHoverBg: "#1d222b",
      outlinedActiveBg: "#0c0e12",
      outlinedDisabledBorder: "#AC7725",
      outlinedDisabledColor: "#AC7725",
      plainActiveBg: "#1d222b",
      plainDisabledColor: "#f2f4f7",
      plainHoverBg: "#242a34",
      plainColor: "#f2f4f7",
      softColor: "#a8b0bd",
      softBg: "#242a34",
      softHoverBg: "#1d222b",
      softActiveColor: "#f2f4f7",
      softActiveBg: "#0c0e12",
      softDisabledColor: "#a8b0bd",
      softDisabledBg: "#1d222b",
    },
    background: {
      // CT Modern dark surfaces (mirrors --ct-* dark tokens).
      default: "#0c0e12",
      body: "#0c0e12",
      surface: "#151920",
      popup: "#1d222b",
      backdrop: "#000000B2",
      tooltip: "#1d222b",
      level1: "#151920",
      level2: "#1d222b",
      level3: "#6b7380",
      chatMessage: "#FF6B1A33",
    },
    text: {
      primary: "#f2f4f7",
      secondary: "#a8b0bd",
      tertiary: "#8b93a2",
      icon: "#8b93a2",
      solid: "#201503",
    },
    marker: {
      selected: alpha("#FFF", 0.5),
    },
    divider: "#242a33",
  },
  typography: {
    fontFamily: 'var(--ct-font-sans, "IBM Plex Sans"), "Segoe UI", "Helvetica Neue", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"',
    // Base 13px — matches the TTX design prototype's compact enterprise scale
    // (body 12.5–13px). Scales all MUI component text proportionally (rem-based).
    fontSize: 13,
  },
}

// Default ChaosTrack accent. Used to detect "no override" so the curated
// amber/gold shades in the base themes are kept pixel-identical by default.
export const DEFAULT_BRAND_COLOR = "#F6AB36"

// Return a copy of `theme` with the accent palette family swapped for a
// per-org `brandColor`. Hover/active/disabled shades are derived so a single
// hex picks a coherent set; solid text color is contrast-derived so light
// brand colors don't produce white-on-light CTAs. Returns the theme unchanged
// when no brand color is set or it equals the default, preserving the
// hand-tuned amber/gold values exactly.
export const withBrandColor = (theme, brandColor) => {
  if (!brandColor || brandColor.toLowerCase() === DEFAULT_BRAND_COLOR.toLowerCase()) return theme
  const primary = theme.palette.primary
  const solidColor = getContrastRatio(brandColor, "#fff") >= 3 ? "#fff" : "#1a1405"
  return {
    ...theme,
    palette: {
      ...theme.palette,
      primary: {
        ...primary,
        main: brandColor,
        solidBg: brandColor,
        solidColor,
        solidHoverBg: lighten(brandColor, 0.2),
        solidActiveBg: darken(brandColor, 0.3),
        solidDisabledBg: darken(brandColor, 0.3),
        solidDisabledColor: solidColor,
        outlinedColor: brandColor,
        outlinedBorder: brandColor,
        outlinedHoverBg: alpha(brandColor, 0.08),
        outlinedDisabledBorder: darken(brandColor, 0.3),
        outlinedDisabledColor: darken(brandColor, 0.3),
      },
    },
  }
}

// CT Modern light palette (TTX prototype). Values mirror src/themes/design-tokens.scss
// (--ct-*) — duplicated as hex because MUI's palette math (createTheme augmentation,
// withBrandColor's darken/lighten) needs parseable colors, not CSS vars.
// The token file is the source of truth; keep these in sync.
export const LIGHT_THEME = {
  palette: {
    mode: "light",
    primary: {
      // Deep gold for text/icon usage (readable on white); deep amber for
      // solid CTAs with white ink — mirrors --ct-gold-deep / --ct-accent.
      main: "#9a5b06",
      solidBg: "#e8890c",
      solidColor: "#fff",
      solidHoverBg: "#cf7607",
      solidActiveBg: "#b56708",
      solidDisabledBg: "#f3d9ae",
      solidDisabledColor: "#b08a52",
      outlinedColor: "#9a5b06",
      outlinedBorder: "#f0d49c",
      outlinedHoverBg: "#fdf1dc",
      outlinedActiveBg: "#FFF",
      outlinedDisabledColor: "#cbb98f",
      outlinedDisabledBorder: "#e8dcc2",
      plainHoverBg: "#f0f2f5",
      plainActiveBg: "#e4e7ec",
      plainDisabledColor: "#98a2b3",
      plainColor: "#101828",
      softColor: "#101828",
      softBg: "#eef0f4",
      softHoverBg: "#e4e7ec",
      softActiveColor: "#101828",
      softActiveBg: "#FFF",
      softDisabledColor: "#98a2b3",
      softDisabledBg: "#fafbfc",
    },
    background: {
      default: "#f7f8fa",
      body: "#f7f8fa",
      surface: "#FFF",
      popup: "#FFF",
      backdrop: "rgba(16, 24, 40, 0.35)",
      tooltip: "#f0f2f5",
      level1: "#fafbfc",
      level2: "#f0f2f5",
      level3: "#e4e7ec",
      chatMessage: "#FF6B1A33",
    },
    text: {
      primary: "#101828",
      secondary: "#475467",
      tertiary: "#667085",
      icon: "#475467",
      solid: "#FFF",
    },
    marker: {
      selected: alpha("#101828", 0.5),
    },
    divider: "#e4e7ec",
  },
  typography: {
    fontFamily: 'var(--ct-font-sans, "IBM Plex Sans"), "Segoe UI", "Helvetica Neue", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"',
    // Base 13px — matches the TTX design prototype's compact enterprise scale
    // (body 12.5–13px). Scales all MUI component text proportionally (rem-based).
    fontSize: 13,
  },
}
